import { useMemo } from "react";
import type { WeeklyReport } from "@/types/WeeklyReport";
import {
  resolveReportStatus,
  isDraft,
  isSent,
  isFailed,
  getStatusLabel,
  getStatusColor,
} from "@/utils/statusResolution";

/**
 * Hook to get resolved draft status and utilities for a report
 * Handles backward compatibility with legacy sentByEmail field
 */
export function useDraftStatus(report?: WeeklyReport | null) {
  return useMemo(() => {
    if (!report) {
      return {
        status: "draft" as const,
        label: "Unknown",
        color: "gray" as const,
        isDraft: false,
        isSent: false,
        isFailed: false,
        isResumable: false,
      };
    }

    const status = resolveReportStatus(report);
    const label = getStatusLabel(report);
    const color = getStatusColor(report);
    const draft = isDraft(report);
    const sent = isSent(report);
    const failed = isFailed(report);

    return {
      status,
      label,
      color,
      isDraft: draft,
      isSent: sent,
      isFailed: failed,
      isResumable: draft || failed,
    };
  }, [report]);
}

/**
 * Hook to filter and sort reports by draft status
 */
export function useDraftReports(reports?: WeeklyReport[]) {
  return useMemo(() => {
    if (!reports) return { drafts: [], sent: [], failed: [] };

    const drafts = reports.filter((r) => isDraft(r));
    const sent = reports.filter((r) => isSent(r));
    const failed = reports.filter((r) => isFailed(r));

    // Sort by updatedAt or date (most recent first)
    const sortByDate = (a: WeeklyReport, b: WeeklyReport) => {
      const aTime = new Date(a.updatedAt || a.date || 0).getTime();
      const bTime = new Date(b.updatedAt || b.date || 0).getTime();
      return bTime - aTime;
    };

    return {
      drafts: drafts.sort(sortByDate),
      sent: sent.sort(sortByDate),
      failed: failed.sort(sortByDate),
    };
  }, [reports]);
}
